import { RAIL_WIDTH } from '../../layout/breakpoints';
import type { BusyFilter, IndexSummary } from '../../api/summary';
import type { Destination } from '../../screens/Index';

/** Section navigation as a left rail, which is what the tab bar becomes at the
 *  two-lane breakpoint.
 *
 *  Mirrors the wide frame in the design handoff, which is a good deal more than
 *  a tab bar turned sideways: the journal identifies itself and says when it last
 *  synced, `GO TO` carries the number shortcuts, and `COLLECTIONS` and `FILTERS`
 *  list what this journal actually holds with a live count against each. That
 *  last part is the point — the same principle as the Index, where every row is a
 *  fact about *this* journal rather than a feature name.
 *
 *  Labels only, no icons — the spec is explicit, and it matches the product's
 *  refusal to decorate. Same accessible shape as `TabBarNav`: a labelled nav of
 *  real buttons with `aria-current` on the active one.
 */
export function SectionRail({
  tabs,
  active,
  onSelect,
  onOpen,
  collection,
  summary,
  collections,
}: {
  tabs: string[];
  active: string;
  onSelect: (tab: string) => void;
  /** Collections and filters open through the same route the Index uses. */
  onOpen: (where: Destination, filter?: BusyFilter) => void;
  /** Which collection is open, so the rail can mark it rather than leaving the
   *  selection on `INDEX` while a collection fills the page. */
  collection: Destination | null;
  summary: IndexSummary | null;
  /** Which collections the server has enabled. A disabled one is absent, not
   *  greyed — the Index does the same, and the rail must not offer a route that
   *  answers 404. */
  collections: Record<string, boolean>;
}) {
  interface Row {
    key: Destination;
    label: string;
    meta: string | null;
    /** False for a surface that is listed but not built. It reads as planned
     *  rather than broken, and it cannot be tapped. */
    enabled?: boolean;
  }
  const row = (key: Destination, label: string, meta: string | null, enabled = true): Row => ({
    key,
    label,
    meta,
    enabled,
  });
  const enabled: Row[] = [
    collections['habits'] ? row('habits', 'Habits', numeric(summary?.habitCount)) : null,
    collections['reading_log'] ? row('reading', 'Reading Log', numeric(summary?.readingTitles)) : null,
    collections['gratitude_log']
      ? row('gratitude', 'Gratitude', numeric(summary?.gratitudeWritten))
      : null,
    collections['commonplace_books'] ? row('commonplace', 'Commonplace', null) : null,
    row('future', 'Future Log', null),
  ].filter((entry): entry is Row => entry !== null);

  return (
    <nav
      aria-label="Sections"
      style={{
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: 0,
        width: RAIL_WIDTH,
        paddingTop: 'calc(var(--bujo-gutter) + env(safe-area-inset-top))',
        paddingBottom: 'calc(var(--bujo-space-4) + env(safe-area-inset-bottom))',
        borderRight: '1px solid var(--bujo-rule-heavy)',
        background: 'var(--bujo-surface-page)',
        display: 'flex',
        flexDirection: 'column',
        // Enough collections and filters and this stops fitting; it scrolls
        // inside itself rather than clipping, like the habit grid.
        overflowY: 'auto',
        zIndex: 10,
      }}
    >
      <div style={{ padding: '0 var(--bujo-gutter)' }}>
        {/* An eyebrow, as the frame draws it — the journal naming itself above
            its address, not a wordmark. */}
        <div style={{ font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)', letterSpacing: 'var(--bujo-ls-eyebrow)', color: 'var(--bujo-text-primary)' }}>
          BUJO
        </div>
        <div style={{ font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1.5 var(--bujo-font-mono)', color: 'var(--bujo-text-meta)', paddingTop: 'var(--bujo-space-1)' }}>
          {summary?.server ?? '—'}
        </div>
        <div style={{ font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1.5 var(--bujo-font-mono)', color: 'var(--bujo-text-dim)' }}>
          {summary ? `synced ${summary.syncedAt}` : 'not synced'}
        </div>
      </div>

      <Group label="GO TO">
        {tabs.map((tab, index) => (
          <Row
            key={tab}
            label={titleCase(tab)}
            // The number *is* the shortcut, so it is only printed where the
            // binding exists — the first nine.
            meta={index < 9 ? String(index + 1) : null}
            current={tab === active && !collection}
            onPress={() => onSelect(tab)}
          />
        ))}
      </Group>

      {enabled.length ? (
        <Group label="COLLECTIONS">
          {enabled.map((row) => (
            <Row
              key={row.key}
              label={row.label}
              meta={row.meta}
              current={collection === row.key}
              disabled={row.enabled === false}
              onPress={() => onOpen(row.key)}
            />
          ))}
        </Group>
      ) : null}

      {summary?.busiest.length ? (
        <Group label="FILTERS">
          {summary.busiest.map((filter) => (
            <Row
              key={filter.token}
              label={filter.token}
              meta={String(filter.openTasks)}
              current={false}
              onPress={() => onOpen('filters', filter)}
            />
          ))}
        </Group>
      ) : null}
    </nav>
  );
}

/** A titled block, ruled off from the one before it. */
function Group({ label, children }: { label: string; children: unknown }) {
  return (
    <section
      style={{
        marginTop: 'var(--bujo-space-5)',
        paddingTop: 'var(--bujo-space-4)',
        borderTop: '1px solid var(--bujo-rule-hairline)',
      }}
    >
      <div
        style={{
          padding: '0 var(--bujo-gutter) var(--bujo-space-2)',
          font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
          letterSpacing: 'var(--bujo-ls-label)',
          color: 'var(--bujo-label)',
        }}
      >
        {label}
      </div>
      {children as never}
    </section>
  );
}

function Row({
  label,
  meta,
  current,
  disabled = false,
  onPress,
}: {
  label: string;
  meta: string | null;
  current: boolean;
  disabled?: boolean;
  onPress: () => void;
}) {
  return (
    <button
      type="button"
      aria-current={current ? 'page' : undefined}
      disabled={disabled}
      onClick={onPress}
      style={{
        // 44px is unchanged on a tablet: a keyboard does not remove the finger.
        minHeight: 'var(--bujo-row-min)',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--bujo-space-2)',
        textAlign: 'left',
        cursor: disabled ? 'default' : 'pointer',
        border: 0,
        // The selected row is marked three ways, never by colour alone: an amber
        // edge, a tinted ground, and the weight of the label.
        borderLeft: current
          ? 'var(--bujo-select-edge-width) solid var(--bujo-select-edge)'
          : 'var(--bujo-select-edge-width) solid transparent',
        background: current ? 'var(--bujo-select-ground)' : 'none',
        padding: '0 var(--bujo-gutter) 0 calc(var(--bujo-gutter) - var(--bujo-select-edge-width))',
        font: `${current ? 'var(--bujo-fw-bold)' : 'var(--bujo-fw-regular)'} var(--bujo-fs-meta)/var(--bujo-lh-tight) var(--bujo-font-mono)`,
        color: disabled
          ? 'var(--bujo-text-faint)'
          : current
            ? 'var(--bujo-accent)'
            : 'var(--bujo-text-meta)',
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      {meta ? (
        <span
          style={{
            flex: 'none',
            font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
            color: 'var(--bujo-text-dim)',
          }}
        >
          {meta}
        </span>
      ) : null}
    </button>
  );
}

/** `TODAY` → `Today`. The rail's rows are named, not shouted; only its section
 *  labels are all-caps. */
function titleCase(tab: string): string {
  return tab.charAt(0) + tab.slice(1).toLowerCase();
}

const numeric = (value: number | null | undefined): string | null =>
  typeof value === 'number' ? String(value) : null;
